import { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { getTeacherFreePeriodsDirectReport, type FreePeriodData } from '@timetable/api';

interface FreePeriodReportProps {
  academicYear: string | null;
  versionValidFrom: string | null;
}

export function FreePeriodReport({ academicYear, versionValidFrom }: FreePeriodReportProps) {
  const { data: freePeriodData, isLoading, error } = useQuery({
    queryKey: ['free-period-report', academicYear, versionValidFrom],
    queryFn: () => getTeacherFreePeriodsDirectReport(
      academicYear ?? undefined,
      versionValidFrom ?? undefined
    ),
    enabled: !!academicYear && !!versionValidFrom,
  });

  const { sortedData, stats } = useMemo(() => {
    if (!freePeriodData || freePeriodData.length === 0) {
      return { sortedData: [], stats: { mostFree: null, leastFree: null, average: 0 } };
    }

    const sorted = [...freePeriodData].sort((a, b) => b.total_free_periods - a.total_free_periods);
    const totalFree = sorted.reduce((sum, t) => sum + t.total_free_periods, 0);
    const average = totalFree / sorted.length;

    return {
      sortedData: sorted,
      stats: {
        mostFree: sorted[0],
        leastFree: sorted[sorted.length - 1],
        average: Math.round(average * 10) / 10,
      },
    };
  }, [freePeriodData]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load free period report. Please try again.'}
      </Alert>
    );
  }

  if (!freePeriodData || freePeriodData.length === 0) {
    return (
      <EmptyState
        title="No Data Available"
        description="No free period data found for the selected period."
      />
    );
  }

  return (
    <Box>
      {/* Summary Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Free Period Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Average Free Periods
            </Typography>
            <Typography variant="h4">{stats.average}</Typography>
            <Typography variant="caption" color="text.secondary">
              per teacher
            </Typography>
          </Box>
          {stats.mostFree && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Most Free Periods
              </Typography>
              <Typography variant="h4">{stats.mostFree.total_free_periods}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.mostFree.teacher_name}
              </Typography>
            </Box>
          )}
          {stats.leastFree && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Least Free Periods
              </Typography>
              <Typography variant="h4">{stats.leastFree.total_free_periods}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.leastFree.teacher_name}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Detailed Table with Day-wise Breakdown */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Free Period Distribution by Teacher
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher Name</TableCell>
                <TableCell align="center">Total Free</TableCell>
                <TableCell align="center">Monday</TableCell>
                <TableCell align="center">Tuesday</TableCell>
                <TableCell align="center">Wednesday</TableCell>
                <TableCell align="center">Thursday</TableCell>
                <TableCell align="center">Friday</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((teacher) => (
                <TableRow key={teacher.teacher_id}>
                  <TableCell>
                    {teacher.teacher_name}
                    {teacher.teacher_id === stats.mostFree?.teacher_id && (
                      <Chip
                        label="Most Free"
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {teacher.teacher_id === stats.leastFree?.teacher_id && (
                      <Chip
                        label="Least Free"
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <strong>{teacher.total_free_periods}</strong>
                  </TableCell>
                  <TableCell align="center">{teacher.monday_free}</TableCell>
                  <TableCell align="center">{teacher.tuesday_free}</TableCell>
                  <TableCell align="center">{teacher.wednesday_free}</TableCell>
                  <TableCell align="center">{teacher.thursday_free}</TableCell>
                  <TableCell align="center">{teacher.friday_free}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
